import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CMMC_LEVELS, SSP_STATUSES, AI_NOTICE, PARTNER_TYPES } from "@/lib/reconConstants";
import { ClipboardCheck, ArrowLeft, ArrowRight, Upload, CheckCircle2, FileText, AlertTriangle, Loader2, Search } from "lucide-react";

const OPTIONS = [
  ["extract_controls", "Extract control references"],
  ["extract_claims", "Extract technical implementation claims"],
  ["compare", "Compare report claims against SSP statements"],
  ["missing_evidence", "Identify missing evidence"],
  ["contradictions", "Identify contradictory statements"],
  ["incomplete", "Identify incomplete implementation"],
  ["responsible", "Recommend responsible party"],
  ["suggest_evidence", "Suggest required evidence"],
  ["questions", "Suggest follow-up questions"],
  ["ssp_updates", "Suggest SSP updates"],
  ["poam", "Suggest POA&M candidates"],
];

export default function NewReconciliation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState(null);
  const [partners, setPartners] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [me, setMe] = useState(null);

  const [sspDoc, setSspDoc] = useState(null);
  const [sspSource, setSspSource] = useState("existing");
  const [reportDoc, setReportDoc] = useState(null);
  const [framework, setFramework] = useState("cmmc_l2");
  const [partnerId, setPartnerId] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [options, setOptions] = useState(Object.fromEntries(OPTIONS.map(([k]) => [k, true])));
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/recon/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/recon/frameworks").then((r) => setFrameworks(r.data)).catch(() => {});
    api.get("/recon/me").then((r) => { setMe(r.data); setReviewer(r.data.email); }).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/recon/customers", { params: { q } }).then((r) => setCustomers(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const preId = searchParams.get("customer");
    if (!preId || customer) return;
    api.get("/recon/customers", { params: { q: "" } })
      .then((r) => { const c = (r.data || []).find((x) => x.id === preId); if (c) setCustomer(c); })
      .catch(() => {});
  }, [searchParams, customer]);

  const saveCompliance = async (patch) => {
    setCustomer((c) => ({ ...c, ...patch }));
    try { await api.put(`/recon/customers/${customer.id}/compliance`, patch); } catch { /* silent */ }
  };

  const submit = async () => {
    if (!ack) { toast.error("Please acknowledge the AI review notice."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/recon/reconciliations", {
        customer_id: customer.id, framework,
        ssp_document_id: sspSource === "none" ? null : (sspDoc ? sspDoc.id : null),
        ssp_source_status: sspSource === "none" ? "No Existing SSP" : "Use Existing SSP",
        ssp_source_type: sspSource === "none" ? "None" : "Document",
        report_document_id: reportDoc.id, implementation_partner_id: partnerId || null,
        assigned_reviewer: reviewer, options, acknowledged: true,
      });
      await api.post(`/recon/reconciliations/${res.data.id}/analyze`);
      toast.success("Analysis started");
      navigate(`/operations/reconciliation/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create reconciliation"); }
    finally { setSubmitting(false); }
  };

  const canNext = { 1: !!customer, 2: sspSource === "none" || !!(sspDoc && sspDoc.ready), 3: !!(reportDoc && reportDoc.ready), 4: !!framework, 5: ack }[step];
  const STEPS = ["Customer", "SSP Source", "Implementation Report", "Framework", "Processing"];

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · New Reconciliation</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-gold" aria-hidden="true" /> New Reconciliation
        </h1>
        <div className="flex flex-wrap gap-2 mt-4">
          {STEPS.map((s, i) => (
            <div key={s} data-testid={`wizard-step-${i + 1}`} className={`text-xs px-3 py-1.5 rounded-full border ${step === i + 1 ? "bg-gold text-navy border-gold font-semibold" : step > i + 1 ? "bg-emerald-500/20 text-emerald-100 border-emerald-400/30" : "bg-white/5 text-slate-300 border-white/15"}`}>
              {step > i + 1 && <CheckCircle2 className="h-3 w-3 inline mr-1" />}{i + 1}. {s}
            </div>
          ))}
        </div>
      </header>

      <div className="p-6 md:p-8 max-w-4xl">
        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-customer">
            <h2 className="font-display font-bold text-navy text-lg">Select Customer</h2>
            <div className="relative max-w-md">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input data-testid="customer-search" className="pl-8" placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto">
              {customers.map((c) => (
                <button key={c.id} data-testid={`customer-option-${c.id}`} onClick={() => setCustomer(c)}
                  className={`text-left rounded border p-3 transition-colors ${customer?.id === c.id ? "border-gold bg-gold/10" : "border-border hover:bg-slate-50"}`}>
                  <div className="font-medium text-navy text-sm">{c.company_name}</div>
                  <div className="text-xs text-muted-foreground">{c.cage_code ? `CAGE ${c.cage_code}` : "No CAGE"} · {c.bucket || "—"}</div>
                </button>
              ))}
            </div>
            {customer && (
              <div className="rounded-md border border-border bg-slate-50/60 p-4 grid sm:grid-cols-3 gap-3" data-testid="customer-compliance">
                <div className="space-y-1.5"><Label className="text-xs">CMMC Level</Label>
                  <Select value={customer.cmmc_level || ""} onValueChange={(v) => saveCompliance({ cmmc_level: v })}>
                    <SelectTrigger data-testid="customer-cmmc"><SelectValue placeholder="Set level" /></SelectTrigger>
                    <SelectContent>{CMMC_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">SSP Status</Label>
                  <Select value={customer.ssp_status || ""} onValueChange={(v) => saveCompliance({ ssp_status: v })}>
                    <SelectTrigger data-testid="customer-ssp-status"><SelectValue placeholder="Set status" /></SelectTrigger>
                    <SelectContent>{SSP_STATUSES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Assigned ComplyX Lead</Label>
                  <Input data-testid="customer-lead" value={customer.complyx_lead || ""} onChange={(e) => setCustomer({ ...customer, complyx_lead: e.target.value })} onBlur={(e) => saveCompliance({ complyx_lead: e.target.value })} placeholder="name@complyxgov.com" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-ssp-source">
            <div className="grid sm:grid-cols-2 gap-3">
              <button type="button" data-testid="ssp-source-existing" onClick={() => setSspSource("existing")}
                className={`text-left rounded-md border p-4 transition-colors ${sspSource === "existing" ? "border-gold bg-gold/10" : "border-border hover:bg-slate-50"}`}>
                <div className="font-semibold text-navy text-sm">Use Existing SSP</div>
                <div className="text-xs text-muted-foreground mt-1">Upload the customer's current System Security Plan (PDF/DOCX) or select an existing one.</div>
              </button>
              <button type="button" data-testid="ssp-source-none" onClick={() => setSspSource("none")}
                className={`text-left rounded-md border p-4 transition-colors ${sspSource === "none" ? "border-gold bg-gold/10" : "border-border hover:bg-slate-50"}`}>
                <div className="font-semibold text-navy text-sm">No Existing SSP Yet</div>
                <div className="text-xs text-muted-foreground mt-1">This customer does not yet have an SSP. Reconciliation continues from the other sources; findings can later seed the first SSP.</div>
              </button>
            </div>
            {sspSource === "existing" ? (
              <UploadStep title="Upload SSP" docType="ssp" customerId={customer.id} doc={sspDoc} setDoc={setSspDoc} testid="ssp" />
            ) : (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="ssp-source-none-notice">
                No SSP upload is required. SSP Accuracy will show <b>Not Yet Evaluated — No SSP Exists</b> and will not be treated as a deficiency. You can continue to the next step.
              </div>
            )}
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-report">
            <div className="space-y-1.5 max-w-md">
              <Label className="text-xs">Implementation Partner</Label>
              <Select value={partnerId} onValueChange={setPartnerId}>
                <SelectTrigger data-testid="report-partner"><SelectValue placeholder="Select partner (optional)" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <UploadStep title="Upload Implementation Report" docType="report" customerId={customer.id} doc={reportDoc} setDoc={setReportDoc} testid="report" />
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-framework">
            <h2 className="font-display font-bold text-navy text-lg">Select Framework</h2>
            <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              {frameworks.map((f) => (
                <button key={f.id} data-testid={`framework-${f.id}`} onClick={() => setFramework(f.id)}
                  className={`text-left rounded border p-4 ${framework === f.id ? "border-gold bg-gold/10" : "border-border hover:bg-slate-50"}`}>
                  <div className="font-medium text-navy">{f.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{f.version} · {f.control_count} controls</div>
                </button>
              ))}
            </div>
            <div className="space-y-1.5 max-w-md pt-2">
              <Label className="text-xs">Assigned Reviewer *</Label>
              <Input data-testid="framework-reviewer" value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="reviewer@complyxgov.com" />
            </div>
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div className="space-y-4" data-testid="step-processing">
            <h2 className="font-display font-bold text-navy text-lg">Processing Options</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {OPTIONS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 rounded border border-border p-2.5 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={options[k]} onCheckedChange={(v) => setOptions((o) => ({ ...o, [k]: !!v }))} data-testid={`opt-${k}`} />
                  <span className="text-sm text-navy">{label}</span>
                </label>
              ))}
            </div>
            <div className="rounded-md border-l-4 border-l-gold bg-gold/10 p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-gold-dark shrink-0 mt-0.5" />
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="ack-checkbox" className="mt-0.5" />
                <span className="text-sm text-navy">I understand that AI-generated findings require ComplyX review and approval before they become part of the official customer record.</span>
              </label>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-8 pt-4 border-t border-border">
          <Button variant="outline" data-testid="wizard-back" onClick={() => step === 1 ? navigate("/operations/reconciliation") : setStep(step - 1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < 5 ? (
            <Button data-testid="wizard-next" disabled={!canNext} onClick={() => setStep(step + 1)} className="bg-navy text-white hover:bg-navy-700">
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button data-testid="wizard-analyze" disabled={!ack || submitting} onClick={submit} className="bg-gold text-navy hover:bg-gold-dark font-semibold">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</> : "Analyze Documents"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadStep({ title, docType, customerId, doc, setDoc, testid }) {
  const [meta, setMeta] = useState({ title: "", version: "", doc_date: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [maxMb, setMaxMb] = useState(50);
  const [uploadKey, setUploadKey] = useState("");
  useEffect(() => { api.get("/recon/config").then((r) => setMaxMb(r.data.max_upload_mb)).catch(() => {}); }, []);

  // Poll processing status until the document is ready or failed — never blocks the upload request.
  useEffect(() => {
    if (!doc || !doc.id) return;
    if (doc.ready || doc.processing_stage === "Failed") return;
    let active = true;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/recon/documents/${doc.id}/status`);
        if (!active) return;
        setDoc(r.data);
        if (r.data.ready || r.data.processing_stage === "Failed") clearInterval(iv);
      } catch { /* keep polling */ }
    }, 2000);
    return () => { active = false; clearInterval(iv); };
  }, [doc?.id, doc?.ready, doc?.processing_stage, setDoc]);

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx"].includes(ext)) { toast.error("Only PDF or DOCX files are accepted."); return; }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`File is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed is ${maxMb} MB.`);
      return;
    }
    setUploading(true);
    const key = `${customerId}-${docType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploadKey(key);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("title", meta.title || file.name);
    fd.append("version", meta.version);
    fd.append("doc_date", meta.doc_date);
    fd.append("customer_id", customerId);
    fd.append("notes", meta.notes);
    fd.append("upload_key", key);
    try {
      const res = await api.post("/recon/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(res.data);
      toast.success("Your file was uploaded successfully and is being processed. You may remain on this page or return later.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const retry = async () => {
    try { const r = await api.post(`/recon/documents/${doc.id}/retry`); setDoc(r.data); toast.info("Reprocessing the document…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };

  const stage = doc?.processing_stage;
  const failed = stage === "Failed";
  const review = stage === "Extraction Review Required";
  const processing = doc && !doc.ready && !failed;

  return (
    <div className="space-y-4" data-testid={`step-${testid}`}>
      <h2 className="font-display font-bold text-navy text-lg">{title}</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid={`${testid}-title`} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Version *</Label><Input data-testid={`${testid}-version`} value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} placeholder="e.g. 1.0" /></div>
        <div className="space-y-1.5"><Label className="text-xs">{docType === "ssp" ? "Effective date" : "Report date"}</Label><Input type="date" data-testid={`${testid}-date`} value={meta.doc_date} onChange={(e) => setMeta({ ...meta, doc_date: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Internal notes</Label><Textarea data-testid={`${testid}-notes`} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} rows={2} /></div>

      {!doc ? (
        <label className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border p-8 cursor-pointer hover:bg-slate-50" data-testid={`${testid}-dropzone`}>
          {uploading ? <><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /><span className="text-sm text-muted-foreground">Uploading…</span></>
            : <><Upload className="h-6 w-6 text-gold-dark" /><span className="text-sm text-navy font-medium">Click to upload PDF or DOCX</span><span className="text-xs text-muted-foreground">Original file is preserved in secure storage · max {maxMb} MB</span></>}
          <input type="file" accept=".pdf,.docx" className="hidden" data-testid={`${testid}-file-input`} onChange={(e) => upload(e.target.files[0])} />
        </label>
      ) : (
        <div className="rounded-md border border-border p-4 space-y-3" data-testid={`${testid}-uploaded`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-navy" />
              <div><div className="font-medium text-navy text-sm">{doc.title}</div><div className="text-xs text-muted-foreground">{doc.original_filename} · {(doc.size / 1024).toFixed(0)} KB{doc.page_count ? ` · ${doc.page_count} pages` : ""}</div></div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] px-2 py-0.5 rounded ${doc.ready && !review ? "bg-emerald-100 text-emerald-800" : failed ? "bg-red-100 text-red-800" : review ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`} data-testid={`${testid}-stage`}>
                {stage || "Queued"}
              </span>
              {failed && <Button size="sm" variant="outline" data-testid={`${testid}-retry`} onClick={retry}>Retry</Button>}
              <Button size="sm" variant="ghost" data-testid={`${testid}-replace`} onClick={() => setDoc(null)}>Replace</Button>
            </div>
          </div>

          {processing && (
            <div className="space-y-1.5" data-testid={`${testid}-progress`}>
              <div className="flex items-center gap-2 text-xs text-navy"><Loader2 className="h-3.5 w-3.5 animate-spin text-gold-dark" /> Upload complete — document processing has started. You may leave and return.</div>
              <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-gold transition-all duration-500" style={{ width: `${doc.progress || 5}%` }} /></div>
              <div className="text-[11px] text-muted-foreground">{stage} · {doc.progress || 0}%</div>
            </div>
          )}
          {doc.ready && !review && <div className="text-xs text-emerald-700 flex items-center gap-1" data-testid={`${testid}-ready`}><CheckCircle2 className="h-3.5 w-3.5" /> Ready for review — you can continue.</div>}
          {review && <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Little or no extractable text (image-only PDF?). You may continue, but a searchable PDF is recommended.</div>}
          {failed && <div className="text-xs text-red-700 flex items-center gap-1" data-testid={`${testid}-error`}><AlertTriangle className="h-3.5 w-3.5" /> {doc.error_message || "Processing failed. The original file is preserved — retry or replace."}</div>}
          {doc.duplicate_of && <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Possible duplicate of "{doc.duplicate_of.title}"</div>}
          {(doc.ready || review) && doc.preview && (
            <details className="text-xs"><summary className="cursor-pointer text-gold-dark" data-testid={`${testid}-preview-toggle`}>Preview extracted text</summary>
              <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-3 max-h-48 overflow-auto whitespace-pre-wrap text-[11px]">{doc.preview || "(no preview)"}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

